<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useDisplay } from 'vuetify';
import BottomNavigationBar from './components/bottomNavigationBar.vue';
import { useRoute, useRouter } from 'vue-router';
import axios from '@/axios';
import { useNotificationsStore } from './stores/notifications';
import notifications from './components/notifications.vue';
import SearchComponent from './components/SearchComponent.vue';
import SearchOverlay from './components/SearchOverlay.vue';

const currentUsername = localStorage.getItem('username')

// Search state
const searchOpen = ref(false);

// Search handlers
const openSearch = () => {
  searchOpen.value = true;
};

const handleSearchResult = (result) => {
  console.log('Search result:', result);
  
  // Handle navigation based on result type
  if (result.type === 'user') {
    router.push({ 
      name: 'ProfileBeta', 
      params: { username: result.username } 
    });
  } else if (result.type === 'team') {
    router.push({
      name: 'TeamPage',
      params: { name: result.name, id: result.id }
    });
  } else if (result.type === 'league') {
    router.push({
      name: 'LeaguePage',
      params: { name: result.name, id: result.id }
    });
  }
};

const { width } = useDisplay()
const isXs = computed(() => width.value < 700)

// valida no mount, mas só limpa se 401
const validateAuth = async () => {
  try {
    await axios.get('/auth/validate');
  } catch (error) {
    if (error.response?.status === 401) {
      clearSession();
      console.log('Sessão expirada, usuário deslogado silenciosamente.');
    } else {
      console.error('Erro ao validar sessão:', error);
    }
  }
};

const notificationsStore = useNotificationsStore();

const isLoggedIn = computed(() => !!localStorage.getItem('jwtToken'));

const router = useRouter();
const route = useRoute();

// Defina as rotas nas quais você não quer que o BottomNavigationBar apareça
const hiddenRoutes = ['/login'];

const showNavigationBar = computed(() => !hiddenRoutes.includes(route.path));

const handleLogout = () => {
  // Remover itens do localStorage
  localStorage.removeItem('jwtToken');
  localStorage.removeItem('userId');
  localStorage.removeItem('username');
  localStorage.removeItem('lastLogin');

  // Remover o cookie 'access_token'
  // Importante: Ajuste 'domain' e 'path' para corresponder exatamente
  // aos valores usados na criação do cookie
  document.cookie = "access_token=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=api.futez.com.br;";

  // Redirecionar para a página de login
  router.push('/login');
};

// limpa sessão sem redirecionar
const clearSession = () => {
  localStorage.removeItem('jwtToken');
  localStorage.removeItem('userId');
  localStorage.removeItem('username');
  localStorage.removeItem('lastLogin');
  document.cookie = "access_token=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=api.futez.com.br;";
};

const activeTab = ref('');

const navItems = [
  { name: 'home', label: 'Partidas', route: { name: 'GamesPageBeta' } },
  { name: 'profile', label: 'Perfil', route: { name: 'ProfileBeta', params: { username: currentUsername } } },
  { name: 'feed', label: 'Seguindo', route: { name: 'Feed' } },
];

const filteredNavItems = computed(() =>
  isLoggedIn.value
    ? navItems
    : navItems.filter(item => item.name !== 'profile')
);

watch(
  () => route.name,
  (newRouteName) => {
    const matchingItem = navItems.find(item => item.route.name === newRouteName);
    activeTab.value = matchingItem ? matchingItem.name : '';
  },
  { immediate: true }
);

onMounted(async () => {
  // 1) valida sessão (silencioso)
  await validateAuth();

  // 2) busca notificações só se ainda estiver logado
  if (isLoggedIn.value) {
    try {
      const response = await axios.get('/notifications');
      notificationsStore.setNotifications(response.data);
      console.log('Notificações armazenadas na store:', notificationsStore.notifications);
    } catch (error) {
      console.error('Erro ao buscar notificações:', error);
    }
  }
});

</script>

<template>
  <v-app id="app">
    <!-- App Bar -->
    <v-app-bar app class="app-bar-color" density="compact" elevation="0">
      <v-container class="d-flex align-center" style="max-width: 1000px; margin: 0 auto; padding: 0 8px;">
        
        <!-- TEMPLATE DESKTOP -->
        <template v-if="!isXs">
          <v-row no-gutters class="pb-0 w-100">
            <v-col cols="4" class="d-flex justify-start align-center">
              <v-menu>
                <template v-slot:activator="{ props }">
                  <v-btn color="logo-color" icon v-bind="props" aria-label="Menu">
                    <v-icon>mdi-menu</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item to="/partidas" prepend-icon="mdi-soccer">
                    <v-list-item-title>Partidas</v-list-item-title>
                  </v-list-item>
                  <v-list-item disabled  to="/stadiums" prepend-icon="mdi-stadium">
                    <v-list-item-title>Estádios</v-list-item-title>
                  </v-list-item>
                  <v-list-item disabled to="/teams" prepend-icon="mdi-shield">
                    <v-list-item-title>Times</v-list-item-title>
                  </v-list-item>
                  <template v-if="!isLoggedIn">
                    <v-list-item :to="{ name: 'Login' }" prepend-icon="mdi-login">
                      <v-list-item-title>Login</v-list-item-title>
                    </v-list-item>
                  </template>
                  <template v-else>
                    <v-list-item v-if="isLoggedIn"
                      :to="{ name: 'ProfileBeta', params: { username: currentUsername } }"
                      prepend-icon="mdi-account-circle"
                    >
                      <v-list-item-title>Perfil</v-list-item-title>
                    </v-list-item>
                  </template>
                  <v-divider></v-divider>
                  <v-list-item disabled to="/settings" prepend-icon="mdi-cog">
                    <v-list-item-title>Ajustes</v-list-item-title>
                  </v-list-item>
                  <v-list-item to="/help" prepend-icon="mdi-logout" @click="handleLogout">
                    <v-list-item-title>Sair</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-col>
            <v-col cols="4" class="d-flex justify-center align-center">
              <router-link :to="{ name: 'GamesPageBeta' }" class="text-decoration-none">
                <v-app-bar-title class="logo-style cursor-pointer">
                  FUTEZ
                </v-app-bar-title>
              </router-link>
            </v-col>
            <v-col cols="4" class="d-flex justify-end align-center">
              <SearchComponent @open-search="openSearch" />
              <notifications v-if="isLoggedIn" />
              <v-btn
                v-if="!isLoggedIn"
                icon
                variant="text"
                @click="router.push('/login')"
                class="px-3"
                style="color: var(--cream);"
              >
                <v-icon>mdi-login-variant</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </template>

        <!-- TEMPLATE MOBILE -->
        <template v-else>
          <v-row no-gutters class="pb-0 w-100">
            <!-- Menu Mobile -->
            <v-col cols="3" class="d-flex justify-start align-center">
              <v-menu>
                <template v-slot:activator="{ props }">
                  <v-btn color="logo-color" icon v-bind="props" aria-label="Menu">
                    <v-icon>mdi-menu</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item to="/partidas" prepend-icon="mdi-soccer">
                    <v-list-item-title>Partidas</v-list-item-title>
                  </v-list-item>
                  <v-list-item disabled to="/stadiums" prepend-icon="mdi-stadium">
                    <v-list-item-title>Estádios</v-list-item-title>
                  </v-list-item>
                  <v-list-item disabled to="/teams" prepend-icon="mdi-shield">
                    <v-list-item-title>Times</v-list-item-title>
                  </v-list-item>
                  <v-list-item disabled to="/players" prepend-icon="mdi-account-group">
                    <v-list-item-title>Jogadores</v-list-item-title>
                  </v-list-item>
                  <template v-if="!isLoggedIn">
                    <v-list-item :to="{ name: 'Login' }" prepend-icon="mdi-login">
                      <v-list-item-title>Login</v-list-item-title>
                    </v-list-item>
                  </template>
                  <template v-else>
                    <v-list-item v-if="isLoggedIn"
                      :to="{ name: 'ProfileBeta', params: { username: currentUsername } }"
                      prepend-icon="mdi-account-circle"
                    >
                      <v-list-item-title>Perfil</v-list-item-title>
                    </v-list-item>
                  </template>
                  <v-divider></v-divider>
                  <v-list-item disabled to="/settings" prepend-icon="mdi-cog">
                    <v-list-item-title>Ajustes</v-list-item-title>
                  </v-list-item>
                  <v-list-item to="/help" prepend-icon="mdi-logout" @click="handleLogout">
                    <v-list-item-title>Sair</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-col>

            <!-- Logo Mobile -->
            <v-col cols="6" class="d-flex justify-center align-center">
              <router-link :to="{ name: 'GamesPageBeta' }" class="text-decoration-none">
                <v-app-bar-title class="logo-style cursor-pointer">
                  FUTEZ
                </v-app-bar-title>
              </router-link>
            </v-col>

            <!-- Actions Mobile -->
            <v-col cols="3" class="d-flex justify-end align-center">
              <notifications v-if="isLoggedIn" />
              <v-btn
                v-if="!isLoggedIn"
                icon
                variant="text"
                @click="router.push('/login')"
                class="px-3"
                style="color: var(--cream);"
              >
                <v-icon>mdi-login-variant</v-icon>
              </v-btn> 
            </v-col>
          </v-row>
        </template>
      </v-container>
    </v-app-bar>

    <v-app-bar 
      v-if="!isXs"
      class="toolbar-color" 
      density="compact" 
      elevation="0"
      :stacked="true"
      height="50"
    >
      <v-container class="pa-0 d-flex justify-center align-center" style="max-width: 1000px;">
        <v-tabs
          center-active
          align-tags="center"
          density="compact"
          color="secondary"
        >
          <v-tab 
            v-for="item in filteredNavItems"
            :key="item.name"
            :to="item.route"
            :value="item.name"
            class="tabs-text px-2 mx-8"
            min-width="0"
          >
            {{ item.label }}
          </v-tab>
        </v-tabs>
      </v-container>
    </v-app-bar>

    <!-- Main Content -->
    <v-main class="main-background" style="min-height: 100vh;">
      <router-view :key="$route.fullPath" /> 
    </v-main>

    <!-- Footer com BottomNavigationBar -->
    <BottomNavigationBar 
      v-if="isXs && showNavigationBar"
      @open-search="openSearch"
    />

    <!-- Search Overlay -->
    <SearchOverlay
      v-model="searchOpen"
      @result-click="handleSearchResult"
    />

  </v-app>
</template>

<style scoped>
.max-width-container {
  max-width: 1000px;  
}
.logo {
  font-weight: 700;
  font-style: italic;
  font-size: 1.5rem;
  text-shadow: 1.5px 1.5px 1px var(--off-black);
}
.app-bar-color {
  background-color: #0e343e !important;
  width: 100%;
}

.by-color {
  color: var(--butter-yellow) !important;
}

.color-menu {
  color: var(--butter-yellow) !important;
}

.logo-style {
  color: var(--cream);
  font-weight: 700;
  font-style: italic;
  font-size: 1.5rem;
  text-shadow: 1.5px 1.5px 1px var(--off-black);
}

.search-results {
  background-color: #0B2A33;
  border-bottom-left-radius: 25px;
  border-bottom-right-radius: 25px;
  color: var(--off-white);
}

.username-record {
  font-size: 14px;
  font-family: 'Poppins';
  color: var(--butter-yellow);
  font-weight: 700;
}
.tabs-text {
  color: white !important;
  font-size: 14px;
  font-family: 'Poppins';
  font-weight: 700;
}

.main-background {
  background-color: #14181c;
}

.container-principal {
  background-color: #14181c;
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 16px;
  min-height: 100vh;
}

.swgreen-background-color {
  background-color: var(--seaweed-green) !important;
  color: var(--butter-yellow);
}
.toolbar-color {
  background-color: #1e2227 !important;
}
.v-tab {
  letter-spacing: normal;
  text-transform: none;
  font-weight: 500;
}

/* Active tab indicator */
.v-tab--selected {
  color: var(--cream) !important;
  font-weight: 700 !important;
}
</style>